import { useState, useEffect, useCallback, useRef } from 'react'
import { RouteData } from '@cya-routes/shared'

interface UseRouteSelectionProps {
  routes: RouteData[]
  mapManager: React.MutableRefObject<any>
}

interface UseRouteSelectionReturn {
  selectedRouteId: string
  setSelectedRouteId: (id: string) => void
  zoomToRoute: (routeId: string) => void
}

/**
 * Custom hook that manages route selection state and navigation
 * Handles zooming to routes and managing selected route state
 */
export function useRouteSelection({
  routes,
  mapManager
}: UseRouteSelectionProps): UseRouteSelectionReturn {
  const [selectedRouteId, setSelectedRouteId] = useState<string>('')
  
  // Create a ref for the zoom function so it can be called before mapManager is ready
  const zoomToRouteRef = useRef<(routeId: string) => void>(() => {})
  
  // Wrapper function that calls the ref
  const zoomToRoute = useCallback((routeId: string) => {
    zoomToRouteRef.current(routeId)
  }, [])
  
  // Helper function to calculate bounds for coordinates
  const calculateBounds = (coordinates: [number, number][]) => {
    // Filter out invalid coordinates and ensure proper format
    const validCoords = coordinates.filter(coord => {
      if (!coord) return false
      if (Array.isArray(coord) && coord.length >= 2) {
        const [lng, lat] = coord
        return typeof lng === 'number' && typeof lat === 'number' && 
               !isNaN(lng) && !isNaN(lat) &&
               lng >= -180 && lng <= 180 &&
               lat >= -90 && lat <= 90
      }
      return false
    })
    
    if (validCoords.length === 0) {
      console.error('❌ No valid coordinates found for bounds calculation')
      return null
    }
    
    const lngs = validCoords.map(coord => coord[0])
    const lats = validCoords.map(coord => coord[1])
    
    const bounds = {
      north: Math.max(...lats),
      south: Math.min(...lats),
      east: Math.max(...lngs),
      west: Math.min(...lngs)
    }
    
    console.log('📍 Valid coordinates:', validCoords.length, '/', coordinates.length)
    console.log('📍 Bounds:', bounds)
    
    return bounds
  }
  
  // Update zoom function implementation when mapManager is available
  useEffect(() => {
    zoomToRouteRef.current = (routeId: string) => {
      console.log('🔍 zoomToRoute called with routeId:', routeId)
      
      // Detect if mobile for adjusted padding
      const isMobile = window.innerWidth < 768
      
      // Ensure map is ready
      if (!mapManager.current || !mapManager.current.getMap()) {
        console.log('⏳ Map not ready, waiting...')
        const checkInterval = setInterval(() => {
          if (mapManager.current && mapManager.current.getMap()) {
            clearInterval(checkInterval)
            zoomToRouteRef.current(routeId)
          }
        }, 100)
        setTimeout(() => clearInterval(checkInterval), 5000) // Give up after 5 seconds
        return
      }
      
      if (routeId === 'master-route') {
        // For master route, zoom to all routes combined
        console.log('📍 Handling master route zoom, routes:', routes.length)
        const allCoordinates = routes.flatMap(route => {
          console.log('📍 Route:', route.id, 'coords:', route.coordinates?.length || 0)
          return route.coordinates || []
        })
        console.log('📍 Total coordinates collected:', allCoordinates.length)
        
        if (allCoordinates.length > 0) {
          // Check coordinate format
          console.log('📍 First coordinate sample:', allCoordinates[0])
          console.log('📍 Coordinate is array?', Array.isArray(allCoordinates[0]))
          
          const bounds = calculateBounds(allCoordinates)
          if (bounds && mapManager.current) {
            console.log('📍 Calling fitBounds for master route with bounds:', bounds)
            // Convert bounds to Mapbox format [[west, south], [east, north]]
            const mapboxBounds = [[bounds.west, bounds.south], [bounds.east, bounds.north]]
            mapManager.current.fitBounds(mapboxBounds, {
              padding: isMobile ? { 
                top: 80, 
                bottom: 280,  // More padding on bottom for elevation profile
                left: 60,  // Less padding on mobile
                right: 20
              } : { 
                top: 100, 
                bottom: 250,  // More padding on bottom to shift center up (accounting for elevation profile)
                left: 400,  // More padding on left to shift center right (towards 1/4 mark)
                right: 100
              },
              maxZoom: 13, // Don't zoom in too close
              duration: 1000,
              essential: true // Force animation on mobile devices
            })
            console.log('✅ fitBounds called successfully for master route')
          } else if (!bounds) {
            console.log('❌ Could not calculate valid bounds for master route')
          } else {
            console.log('❌ mapManager.current is null')
          }
        } else {
          console.log('❌ No coordinates found for master route')
        }
        return
      }
      
      const route = routes.find(r => r.id === routeId)
      console.log('🗺️ Found route:', route ? `YES (name: ${route?.name})` : 'NO')
      
      if (route && route.coordinates && route.coordinates.length > 0 && mapManager.current) {
        console.log('📍 Route has', route.coordinates.length, 'coordinates')
        console.log('📍 First coordinate:', route.coordinates[0])
        const bounds = calculateBounds(route.coordinates)
        if (bounds) {
          console.log('📍 Calculated bounds:', bounds)
          console.log('📍 Calling fitBounds...')
          // Convert bounds to Mapbox format [[west, south], [east, north]]
          const mapboxBounds = [[bounds.west, bounds.south], [bounds.east, bounds.north]]
          mapManager.current.fitBounds(mapboxBounds, {
            padding: isMobile ? { 
              top: 80, 
              bottom: 280,  // More padding on bottom for elevation profile
              left: 60,  // Less padding on mobile
              right: 20
            } : { 
              top: 100, 
              bottom: 250,  // More padding on bottom to shift center up (accounting for elevation profile)
              left: 400,  // More padding on left to shift center right (towards 1/4 mark)
              right: 100
            },
            maxZoom: 13, // Don't zoom in too close
            duration: 1000,
            essential: true // Force animation on mobile devices
          })
          console.log('✅ fitBounds called successfully')
        } else {
          console.log('❌ Could not calculate valid bounds for route')
        }
      } else {
        console.log('❌ Cannot zoom - missing requirements:')
        console.log('   - route:', !!route)
        console.log('   - coordinates:', route?.coordinates?.length || 0)
        console.log('   - mapManager:', !!mapManager.current)
      }
    }
  }, [routes, mapManager])
  
  // Auto-select logic is handled by the parent component
  // This hook just manages the state and zoom functionality
  
  return {
    selectedRouteId,
    setSelectedRouteId,
    zoomToRoute
  }
}